'use client';

import { Search } from 'lucide-react';
import * as React from 'react';
import { Button, type ButtonProps } from '@/components/atoms/buttons/Button';
import {
  ToggleGroup,
  ToggleGroupItem,
  type ToggleGroupItemProps,
  type ToggleGroupProps,
} from '@/components/atoms/buttons/Toggle';
import { Input, type InputProps } from '@/components/atoms/forms/Input';
import { Separator } from '@/components/atoms/layout/Separator';
import { Tabs } from '@/components/molecules/navigation/Tabs';
import { cn } from '@/lib/utils';

export type SectionHeaderSize = 'sm' | 'md';
export type SectionHeaderHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type SectionHeaderHeadingTag = `h${SectionHeaderHeadingLevel}`;

export interface SectionHeaderAction
  extends Omit<ButtonProps, 'children' | 'render'> {
  label: React.ReactNode;
}

export interface SectionHeaderToggle
  extends Omit<ToggleGroupItemProps, 'children'> {
  label: React.ReactNode;
  value: string;
}

export interface SectionHeaderToggleGroup
  extends Omit<ToggleGroupProps, 'children' | 'label'> {
  items: SectionHeaderToggle[];
}

export interface SectionHeaderTab {
  label: React.ReactNode;
  value?: string;
  active?: boolean;
  leadingIcon?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export interface SectionHeaderSearchProps
  extends Omit<
    InputProps,
    'size' | 'shape' | 'startIcon' | 'endIcon' | 'appearance'
  > {
  placeholder?: string;
  ariaLabel?: string;
  /** Optional interactive filter control rendered beside the search input */
  filterButton?: React.ReactNode;
}

export interface SectionHeaderProps
  extends Omit<React.ComponentPropsWithoutRef<'header'>, 'title'> {
  /** Section title */
  title: React.ReactNode;
  /** Supporting text below the title */
  supportingText?: React.ReactNode;
  /** Heading/text size from the Figma variants */
  size?: SectionHeaderSize;
  /** Semantic heading level for the section title */
  headingLevel?: SectionHeaderHeadingLevel;
  /** Action buttons rendered to the right of the title block */
  actions?: SectionHeaderAction[];
  /** Optional grouped selection controls rendered as a segmented control */
  toggleGroup?: SectionHeaderToggleGroup;
  /** Optional search input rendered to the right of the title block */
  search?: SectionHeaderSearchProps;
  /** Optional tabs row rendered below the header controls */
  tabs?: SectionHeaderTab[];
  /** Trailing menu control rendered after the action buttons */
  menuButton?: React.ReactNode;
  /** Show the bottom divider */
  showDivider?: boolean;
}

type SectionHeaderControlProps = Pick<
  SectionHeaderProps,
  'actions' | 'search' | 'toggleGroup'
>;

const controlTouchTargetClassName = 'min-h-11 min-w-11 shrink-0';

function getHeadingTag(
  headingLevel: SectionHeaderHeadingLevel,
): SectionHeaderHeadingTag {
  return `h${headingLevel}` as SectionHeaderHeadingTag;
}

function SectionHeaderTouchTargetControl({
  control,
}: {
  control: React.ReactNode;
}): React.ReactNode {
  if (React.isValidElement<{ className?: string }>(control)) {
    return React.cloneElement(control, {
      className: cn(control.props.className, controlTouchTargetClassName),
    });
  }

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        controlTouchTargetClassName,
      )}
    >
      {control}
    </span>
  );
}

function getSectionHeaderActionKey(
  action: SectionHeaderAction,
  index: number,
  prefix = 'section-header-action',
): React.Key {
  return typeof action.label === 'string'
    ? `${action.label}-${index}`
    : `${prefix}-${index}`;
}

function SectionHeaderActionButton({
  action,
  className,
}: {
  action: SectionHeaderAction;
  className?: string;
}): React.JSX.Element {
  const { label, className: actionClassName, ...buttonProps } = action;

  return (
    <Button
      appearance="solid"
      size="default"
      shape="rounded"
      className={cn(
        'h-10 rounded-xl px-2 text-xs font-medium',
        actionClassName,
        className,
      )}
      {...buttonProps}
    >
      {label}
    </Button>
  );
}

function SectionHeaderToggleGroupControl({
  items,
  className,
  ...toggleGroupProps
}: SectionHeaderToggleGroup): React.JSX.Element {
  return (
    <ToggleGroup
      variant="outline"
      shape="rounded"
      className={cn('shrink-0 overflow-hidden rounded-xl', className)}
      {...toggleGroupProps}
    >
      {items.map((item) => {
        const { label, className: itemClassName, ...itemProps } = item;

        return (
          <ToggleGroupItem
            key={item.value}
            className={cn('px-3 text-xs font-medium', itemClassName)}
            {...itemProps}
          >
            {label}
          </ToggleGroupItem>
        );
      })}
    </ToggleGroup>
  );
}

function getTabValue(tab: SectionHeaderTab, index: number): string {
  if (tab.value !== undefined) {
    return tab.value;
  }

  if (typeof tab.label === 'string') {
    return `${tab.label}-${index}`;
  }

  return `section-header-tab-${index}`;
}

function SectionHeaderTabs({
  tabs,
}: {
  tabs: SectionHeaderTab[];
}): React.JSX.Element {
  const tabValues = tabs.map((tab, index) => getTabValue(tab, index));
  const tabListKey = JSON.stringify(tabValues);
  const activeIndex = tabs.findIndex((tab) => tab.active);
  const activeValue = activeIndex >= 0 ? tabValues[activeIndex] : undefined;

  return (
    <Tabs
      key={tabListKey}
      value={activeValue}
      defaultValue={activeValue ?? tabValues[0]}
      className="w-full"
    >
      <Tabs.List
        variant="boxed"
        size="sm"
        align="stretch"
        aria-label="Section header tabs"
        className="w-full justify-center-safe overflow-x-auto overflow-y-hidden rounded-[8px] border border-border bg-card p-1 shadow-sm"
      >
        {tabs.map((tab, index) => {
          const value = tabValues[index];
          return (
            <Tabs.Trigger
              key={value}
              value={value}
              icon={tab.leadingIcon}
              className="min-w-max flex-1 rounded-[10px] px-3 py-1"
              onClick={tab.onClick}
            >
              <span className="whitespace-nowrap">{tab.label}</span>
            </Tabs.Trigger>
          );
        })}
      </Tabs.List>
    </Tabs>
  );
}

function SectionHeaderControls({
  actions,
  search,
  toggleGroup,
}: SectionHeaderControlProps): React.JSX.Element {
  return (
    <React.Fragment>
      {search ? <SectionHeaderSearchField search={search} /> : null}
      {toggleGroup?.items.length ? (
        <SectionHeaderToggleGroupControl {...toggleGroup} />
      ) : null}
      {actions?.length ? (
        <div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
          {actions.map((action, index) => (
            <SectionHeaderActionButton
              key={getSectionHeaderActionKey(action, index)}
              action={action}
            />
          ))}
        </div>
      ) : null}
    </React.Fragment>
  );
}

function SectionHeaderSearchField({
  search,
}: {
  search: SectionHeaderSearchProps;
}): React.JSX.Element {
  const {
    className: searchClassName,
    placeholder,
    ariaLabel,
    filterButton,
    ...searchProps
  } = search;
  const searchInput = (
    <Input
      size="md"
      shape="rounded"
      aria-label={ariaLabel ?? 'Search'}
      placeholder={placeholder ?? 'Search'}
      startIcon={<Search className="size-4" />}
      className={cn(
        filterButton ? 'min-w-0 flex-1' : 'w-full max-w-[320px]',
        searchClassName,
      )}
      {...searchProps}
    />
  );

  if (filterButton) {
    return (
      <div
        data-slot="section-header-search"
        className="flex w-full max-w-[376px] items-center gap-2"
      >
        {searchInput}
        <SectionHeaderTouchTargetControl control={filterButton} />
      </div>
    );
  }

  return searchInput;
}

const SectionHeader = React.forwardRef<HTMLElement, SectionHeaderProps>(
  (
    {
      title,
      supportingText,
      size = 'md',
      headingLevel = 2,
      actions,
      search,
      tabs,
      menuButton = null,
      showDivider = true,
      toggleGroup,
      className,
      ...props
    },
    ref,
  ) => {
    const hasSearch = Boolean(search);
    const hasToggleGroup = Boolean(toggleGroup?.items.length);
    const hasActions = Boolean(actions?.length);
    const hasInlineControls = hasSearch || hasToggleGroup || hasActions;
    const hasHeaderControls = hasInlineControls || Boolean(menuButton);
    const HeadingTag = getHeadingTag(headingLevel);

    return (
      <header
        ref={ref}
        data-slot="section-header"
        className={cn('relative flex w-full flex-col gap-4', className)}
        {...props}
      >
        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-3">
          <div
            className={cn(
              'col-start-1 row-start-1 flex min-w-0 flex-col justify-center gap-0.5',
              menuButton ? 'pe-8 md:pe-0' : null,
            )}
          >
            <HeadingTag
              className={cn(
                'line-clamp-2 w-full break-words font-semibold text-foreground',
                size === 'sm'
                  ? 'text-[14px] leading-5'
                  : 'text-[18px] leading-7',
              )}
            >
              {title}
            </HeadingTag>
            {supportingText ? (
              <p className="line-clamp-2 w-full break-words text-sm leading-5 text-secondary-foreground">
                {supportingText}
              </p>
            ) : null}
          </div>

          {hasHeaderControls ? (
            <div
              className={cn(
                'col-span-2 row-start-2 flex flex-wrap items-center gap-3 md:col-span-1 md:col-start-2 md:row-start-1 md:flex-nowrap md:justify-end',
                !hasInlineControls && 'contents md:flex',
              )}
            >
              <SectionHeaderControls
                actions={actions}
                search={search}
                toggleGroup={toggleGroup}
              />
              {menuButton ? (
                <div className="absolute end-0 top-0 md:static">
                  <SectionHeaderTouchTargetControl control={menuButton} />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {tabs?.length ? <SectionHeaderTabs tabs={tabs} /> : null}

        {showDivider ? <Separator aria-hidden="true" /> : null}
      </header>
    );
  },
);

SectionHeader.displayName = 'SectionHeader';

export { SectionHeader };
